import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FileText, Shield, Cookie, Scale, Users, Globe, ChevronRight, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchLegalDocuments, type LegalDocument } from '@/lib/cms';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

const iconMap: Record<string, React.ElementType> = {
  'terms-conditions': FileText,
  'privacy-policy': Shield,
  'cookie-policy': Cookie,
  ccpa: Scale,
  dpa: Users,
  dcma: Scale,
  'google-api-services-disclosure': Globe,
};

function getIconForSlug(slug: string): React.ElementType {
  return iconMap[slug] || FileText;
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const Legal: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [activeDocument, setActiveDocument] = useState<LegalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadDocuments() {
      try {
        setLoading(true);
        setError(null);
        const docs = await fetchLegalDocuments();
        if (cancelled) return;

        setDocuments(docs);

        if (docs.length > 0) {
          const match = slug ? docs.find((d) => d.slug === slug) : null;
          setActiveDocument(match || docs[0]);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load legal documents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDocuments();
    return () => { cancelled = true; };
  }, [slug]);

  function handleSelectDocument(doc: LegalDocument) {
    setActiveDocument(doc);
    navigate(`/legal/${doc.slug}`, { replace: true });
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Legal', item: '/legal' },
        { name: activeDocument?.title ?? 'Legal' },
      ]} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff]/20 via-transparent to-[#00d4ff]/10" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#635bff]/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#00d4ff]/15 rounded-full blur-3xl animate-float-delayed" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-6">
            <Link to="/resources" className="hover:text-slate-900 dark:hover:text-white transition-colors">Resources</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 dark:text-white">Legal</span>
          </div>
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-1.5 text-sm font-medium text-[#635bff] bg-[#635bff]/10 border border-[#635bff]/20 rounded-full mb-6">
              Legal</span>

            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              Legal Documents
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              Review our terms, policies, and legal agreements that govern your use of Adventive services.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-8 h-8 text-[#635bff] animate-spin" />
              <span className="ml-3 text-slate-600 dark:text-slate-400">Loading legal documents...</span>
            </div>
          ) : error ? (
            <div className="text-center py-32">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[#635bff] text-white rounded-lg hover:bg-[#635bff]/90 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <nav className="sticky top-24 space-y-2">
                  {documents.map((doc) => {
                    const Icon = getIconForSlug(doc.slug);
                    return (
                      <button
                        key={doc.id}
                        onClick={() => handleSelectDocument(doc)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                          activeDocument?.id === doc.id
                            ? 'bg-[#635bff]/20 text-slate-900 dark:text-white border border-[#635bff]/30'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${activeDocument?.id === doc.id ? 'text-[#635bff]' : ''}`} />
                        <span className="font-medium">{doc.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Main Content */}
              <div className="lg:col-span-3">
                {activeDocument && (
                  <div className="bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl p-8 lg:p-12">
                    <div className="mb-8">
                      <div className="flex items-baseline justify-between gap-8">
                        <h2 className="text-3xl font-bold text-slate-900 dark:text-white">{activeDocument.title}</h2>
                        {activeDocument.last_modified && (
                          <span className="text-sm text-slate-500 whitespace-nowrap flex-shrink-0">
                            Last updated: {formatDate(activeDocument.last_modified)}
                          </span>
                        )}
                      </div>
                      {activeDocument.excerpt && (
                        <p className="mt-2 text-slate-600 dark:text-slate-400">{activeDocument.excerpt}</p>
                      )}
                    </div>

                    <div
                      className="prose dark:prose-invert prose-lg max-w-none
                        prose-headings:text-slate-900 dark:prose-headings:text-white prose-headings:font-semibold
                        prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                        prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                        prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-p:leading-relaxed
                        prose-ul:text-slate-700 dark:prose-ul:text-slate-300 prose-li:marker:text-[#635bff]
                        prose-a:text-[#635bff] prose-a:no-underline hover:prose-a:underline"
                      dangerouslySetInnerHTML={{ __html: activeDocument.content }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Legal;
